import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { detectCrisis } from '../services/safety/crisisDetection.js';
import CrisisResourceCard from '../components/CrisisResourceCard.jsx';
import NavMenu from '../components/shared/NavMenu.jsx';
import {
  ChevronLeft,
  Highlighter,
  Bookmark,
  Check,
  BookOpen,
  Send,
  X,
  Play,
  Pause,
  Square,
  Headphones,
  Mic,
  Keyboard,
  ArrowRight,
} from 'lucide-react';
import FontStyles from '../components/shared/FontStyles.jsx';
import FoxMark from '../components/shared/FoxMark.jsx';
import ReactiveFoxMark from '../components/shared/ReactiveFoxMark.jsx';
import RootedWordmark from '../components/shared/RootedWordmark.jsx';
import {
  PAPER,
  PAPER_2,
  INK,
  INK_MID,
  INK_SOFT,
  LINE,
  ACCENT,
  GOLD_DEEP,
  WINE,
} from '../components/shared/theme.js';
import { useApp } from '../context/AppContext.jsx';
import { useSubscription } from '../hooks/useSubscription.js';
import {
  conductDailyCheckin,
  generateNextDay,
} from '../services/anthropic.js';
import { transcribeAudio } from '../services/openai.js';
import { authenticatedFetch } from '../services/apiClient.js';
import {
  speakWithElevenLabs,
  speakIntoElement,
  stopElevenLabsAudio,
  keepAudioSessionAlive,
  releaseAudioSession,
  getAudioSessionState,
} from '../services/elevenlabs.js';
import { getCurrentAmplitude } from '../services/audioAmplitude.js';
import useVoiceConversation from '../hooks/useVoiceConversation.js';

const DAY_COLORS = ['#5B2A2E', '#3A4A52', '#3C3842', '#32436B', '#7A5820'];

// Mirrors api/speak.js's VOICE_IDS map. /api/speak-stream takes a literal
// ElevenLabs voice ID (not a gender alias), so gender → ID resolution
// happens here, matching the pattern used by Intake.jsx.
const VOICE_IDS = {
  male: 'L0Dsvb3SLTyegXwtm47J',
  female: 'ys3XeJJA4ArWMhRpcX1D',
};

// Mobile browsers handle continuous-mode speech recognition poorly and
// silently kill long-running recognition in the background.
const isIOS =
  typeof navigator !== 'undefined' &&
  /iPhone|iPad|iPod/i.test(navigator.userAgent);
const isAndroid =
  typeof navigator !== 'undefined' &&
  /Android/i.test(navigator.userAgent);
const isMobile = isIOS || isAndroid;
const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

// Launch-time gate for the passive "Listen to today's study" feature.
// This is the in-page TTS that streams the full day content (scripture
// + reflection + application + prayer) through ElevenLabs. It racks up
// significant per-user TTS cost at scale, so it's hidden for free-tier
// launch. To re-enable: flip this to true. The underlying read-aloud
// engine (buildReadingQueue, startReading, handleListenPress, the
// StudyAudioPlayer component) is intentionally still present so the
// feature can be turned back on without rewriting anything.
//
// Does NOT affect: voice intake (Talk with Ada), check-in voice
// (CheckinSheet), or any other audio path. Those use distinct
// codepaths and are kept fully working.
const LISTEN_TO_STUDY_ENABLED = false;

// 100-byte silent MP3 used to gesture-unlock the persistent check-in
// audio element inside the "Done & Check-in" tap. iOS unlocks the
// SPECIFIC HTMLAudioElement that played first inside a gesture; once
// unlocked, swapping .src on it works for the whole session even when
// later utterances run after several Whisper getUserMedia cycles.
const SILENT_MP3 =
  'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQwAAAAAAAAAAAAAAAAAAAAAAASW5mbwAAAA8AAAACAAABhgBVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq////////////////////////////////////////////AAAAAExhdmM1OC4xMwAAAAAAAAAAAAAAACQCgAAAAAAAAAGGmGOG3AAAAAAA//tAxAAACMArn3UAgAhTBSh3O0AAEEAQAAGDgGAYBgcAGB4D5SCATBcF/E6XB85znD8H/E5/y/y4Ifk4PggCAIAgD4Pg+/E4f//Lg+CAIBQNg+D4Pg+CAIAgD4Pg+D4f5cHwf/y4P8uCAIAgCAID4Pg+D4Pg+CAIBh//5cHwQBAEAQBAEAQDgIA///UCAIAh//+CAYDg+CAIBQEAQDH//+CAID/+CAIBQEAQDg//9KBAEAYHwQBAEAYHwfB//KAgCAIAgGB8EAQBAEAQDA+D4PggCAYHwfB8EAQBAGB8HwfB8EAYAB';


// =====================================================================
// Render a string with any stored highlight substrings wrapped in gold.
// Exact substring match. Order longest-first so nested highlights win.
// =====================================================================
function renderHighlighted(text, highlights) {
  if (!text || !highlights || highlights.length === 0) return text;
  const sorted = [...highlights].sort((a, b) => b.length - a.length);
  let segments = [{ text, hl: false }];
  for (const h of sorted) {
    const next = [];
    for (const seg of segments) {
      if (seg.hl || !seg.text || !seg.text.includes(h)) {
        next.push(seg);
        continue;
      }
      const parts = seg.text.split(h);
      parts.forEach((p, i) => {
        if (p) next.push({ text: p, hl: false });
        if (i < parts.length - 1) next.push({ text: h, hl: true });
      });
    }
    segments = next;
  }
  return segments.map((s, i) =>
    s.hl ? (
      <mark
        key={i}
        style={{
          background: 'rgba(201,164,94,0.45)',
          color: 'inherit',
          padding: '0 2px',
          borderRadius: 2,
        }}
      >
        {s.text}
      </mark>
    ) : (
      <span key={i}>{s.text}</span>
    )
  );
}

// =====================================================================
// Build a warm personal opening from the guide
// =====================================================================
function buildStudyIntro(day, userProfile, guide) {
  const name = userProfile?.name || 'friend';
  const title = day?.title || "today's study";
  const scripture = day?.scripture_reference || 'scripture';
  const theme = day?.subtitle || '';
  return `Hey ${name}, it's ${guide?.name || 'me'}. Today we're going to dive into ${title}. ${
    theme ? theme + '. ' : ''
  }We'll be spending some time in ${scripture}, and I think this one is going to land in a real way for you. Take a breath, find a comfortable spot, and let's get into it together.`;
}

// =====================================================================
// Native Web Share with clipboard fallback. Used by the pull-quote
// share button and the Day-1 check-in share prompt.
// =====================================================================
async function sharePullQuote(quote, context = {}) {
  const shareText = `"${quote}"\n\nFrom my Rooted daily study.\n\napp.goldenfoxpublishing.com`;

  if (navigator.share) {
    try {
      await navigator.share({
        text: shareText,
        url: 'https://app.goldenfoxpublishing.com',
      });
    } catch (err) {
      if (err.name !== 'AbortError') {
        navigator.clipboard?.writeText(shareText);
      }
    }
  } else {
    navigator.clipboard?.writeText(shareText);
    alert('Copied to clipboard!');
  }
}

// =====================================================================
// Build the ordered reading queue from a day's content
// =====================================================================

// iOS struggles with long single chunks — split at sentence boundaries
// once a chunk crosses ~maxWords. Returns one chunk if the text is short.
function splitLongText(text, maxWords = 80) {
  const words = text.split(' ');
  if (words.length <= maxWords) return [text];

  const chunks = [];
  let current = [];

  for (const word of words) {
    current.push(word);
    if (current.length >= maxWords && word.match(/[.!?]$/)) {
      chunks.push(current.join(' '));
      current = [];
    }
  }

  if (current.length > 0) chunks.push(current.join(' '));
  return chunks;
}

function buildReadingQueue(day, userProfile, guide) {
  const queue = [];
  if (!day) {
    console.warn('[Reading] buildReadingQueue: no day data');
    return queue;
  }

  console.log('[Reading] Building queue from:', {
    title: day.title,
    hasOpeningPrayer: !!day.opening_prayer,
    scriptureTextLength: day.scripture_text?.length || 0,
    contentSections: day.content_sections?.length || 0,
    hasEnneagramInsight: !!day.enneagram_insight,
    hasPullQuote: !!day.pull_quote,
    reflectionQuestions: day.reflection_questions?.length || 0,
    hasClosingPractice: !!day.closing_practice,
  });

  // Personal intro
  const introText = buildStudyIntro(day, userProfile, guide);
  if (introText) {
    queue.push({ label: 'Introduction', text: introText });
    console.log(
      '[Reading] Added: Introduction, words:',
      introText.split(/\s+/).length
    );
  }

  // Opening prayer
  if (day.opening_prayer && day.opening_prayer.trim()) {
    const text = 'Let us begin with a prayer. ' + day.opening_prayer;
    queue.push({ label: 'Opening Prayer', text });
    console.log(
      '[Reading] Added: Opening Prayer, words:',
      text.split(/\s+/).length
    );
  } else {
    console.warn('[Reading] Skipping: Opening Prayer (empty)');
  }

  // Scripture
  if (day.scripture_text && day.scripture_text.trim()) {
    const text = `Today we read from ${day.scripture_reference || 'scripture'}. ${day.scripture_text}`;
    queue.push({
      label: day.scripture_reference || 'Scripture',
      text,
    });
    console.log(
      '[Reading] Added: Scripture (' +
        (day.scripture_reference || 'unspecified') +
        '), words:',
      text.split(/\s+/).length
    );
  } else {
    console.warn('[Reading] Skipping: Scripture (empty)');
  }

  // Content sections — split long sections so no single chunk overruns
  // the iOS audio session. Each split piece becomes its own queue entry.
  if (Array.isArray(day.content_sections)) {
    day.content_sections.forEach((section, i) => {
      if (section && section.body && section.body.trim()) {
        const fullText =
          (section.heading ? section.heading + '. ' : '') + section.body;
        const baseLabel = section.heading || `Section ${i + 1}`;
        const chunks = splitLongText(fullText, 80);
        console.log(
          `[Reading] Section ${i + 1} "${baseLabel}" split into ${chunks.length} chunk(s)`
        );
        chunks.forEach((chunk, ci) => {
          const label = ci === 0 ? baseLabel : `${baseLabel} (continued)`;
          queue.push({ label, text: chunk });
          console.log(
            `[Reading] Added: ${label}, words:`,
            chunk.split(/\s+/).length
          );
        });
      } else {
        console.warn(`[Reading] Skipping empty section ${i}:`, section);
      }
    });
  } else {
    console.warn('[Reading] No content_sections array on day');
  }

  // Enneagram insight
  if (day.enneagram_insight && day.enneagram_insight.trim()) {
    const text =
      'Here is something specific for you. ' + day.enneagram_insight;
    queue.push({ label: 'A Word For You', text });
    console.log(
      '[Reading] Added: Enneagram Insight, words:',
      text.split(/\s+/).length
    );
  } else {
    console.warn('[Reading] Skipping: Enneagram Insight (empty)');
  }

  // Pull quote
  if (day.pull_quote && day.pull_quote.trim()) {
    const text = 'A thought to carry with you today. ' + day.pull_quote;
    queue.push({ label: 'A Thought to Carry', text });
    console.log(
      '[Reading] Added: Pull Quote, words:',
      text.split(/\s+/).length
    );
  } else {
    console.warn('[Reading] Skipping: Pull Quote (empty)');
  }

  // Reflection questions
  if (
    Array.isArray(day.reflection_questions) &&
    day.reflection_questions.length > 0
  ) {
    const filtered = day.reflection_questions.filter((q) => q && q.trim());
    const questions = filtered
      .map((q, i) => `Question ${i + 1}. ${q}`)
      .join('. Take a moment to reflect. ');
    if (questions) {
      const text =
        'Here are your reflection questions for today. ' + questions;
      queue.push({ label: 'Reflection Questions', text });
      console.log(
        '[Reading] Added: Reflection Questions, count:',
        filtered.length,
        'words:',
        text.split(/\s+/).length
      );
    } else {
      console.warn('[Reading] Skipping: Reflection Questions (all empty)');
    }
  } else {
    console.warn('[Reading] Skipping: Reflection Questions (none)');
  }

  // Closing practice
  if (day.closing_practice && day.closing_practice.trim()) {
    const text = 'To close today. ' + day.closing_practice;
    queue.push({ label: 'Closing Practice', text });
    console.log(
      '[Reading] Added: Closing Practice, words:',
      text.split(/\s+/).length
    );
  } else {
    console.warn('[Reading] Skipping: Closing Practice (empty)');
  }

  console.log(
    '[Reading] Queue complete:',
    queue.length,
    'chunks:',
    queue.map((q) => q.label)
  );
  return queue;
}

// =====================================================================
// Audio player bar — top-of-day-content
// =====================================================================
function StudyAudioPlayer({
  guideName,
  dayNumber,
  isReading,
  isPaused,
  currentLabel,
  progress,
  finished,
  readingAmplitude = 0,
  onStart,
  onPause,
  onResume,
  onStop,
}) {
  return (
    <div
      className="w-full"
      style={{
        background: PAPER,
        borderTop: `1px solid ${ACCENT}`,
        borderBottom: `1px solid ${LINE}`,
        boxShadow: '0 6px 18px -14px rgba(166,124,58,0.45)',
      }}
    >
      <div className="w-full max-w-[460px] mx-auto px-5 py-4">
        <div className="flex items-center gap-3">
          <ReactiveFoxMark
            amplitude={readingAmplitude}
            isActive={isReading && !isPaused}
            size={48}
          />

          <div className="flex-1 min-w-0">
            <div
              className="ff-mono"
              style={{
                color: GOLD_DEEP,
                fontSize: 9.5,
                letterSpacing: '0.18em',
              }}
            >
              {finished
                ? 'FINISHED'
                : isReading
                ? `${(guideName || 'YOUR GUIDE').toUpperCase()} IS READING`
                : `LISTEN TO TODAY'S STUDY`}
            </div>
            <div
              className="ff-display"
              style={{
                color: INK,
                fontSize: 14,
                fontWeight: 500,
                marginTop: 2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {finished
                ? `${guideName || 'Your guide'} has completed Day ${dayNumber}`
                : isReading
                ? currentLabel
                  ? `Reading: ${currentLabel}…`
                  : 'Starting…'
                : `Day ${dayNumber}`}
            </div>
          </div>

          <div className="flex items-center gap-1.5 flex-shrink-0">
            {!isReading && !finished && (
              <button
                onClick={onStart}
                aria-label="Start reading"
                className="ff-ui inline-flex items-center gap-1.5 px-4 py-2 rounded-full transition active:scale-95"
                style={{
                  background: ACCENT,
                  color: PAPER,
                  fontSize: 12,
                  fontWeight: 500,
                  boxShadow: '0 6px 16px -8px rgba(166,124,58,0.55)',
                }}
              >
                <Headphones size={13} strokeWidth={1.8} />
                Listen
              </button>
            )}

            {isReading && !isPaused && (
              <button
                onClick={onPause}
                aria-label="Pause"
                className="w-9 h-9 rounded-full flex items-center justify-center transition active:scale-95"
                style={{
                  background: ACCENT,
                  color: PAPER,
                }}
              >
                <Pause size={14} strokeWidth={2} fill={PAPER} />
              </button>
            )}

            {isReading && isPaused && (
              <button
                onClick={onResume}
                aria-label="Resume"
                className="w-9 h-9 rounded-full flex items-center justify-center transition active:scale-95"
                style={{
                  background: ACCENT,
                  color: PAPER,
                }}
              >
                <Play size={14} strokeWidth={2} fill={PAPER} />
              </button>
            )}

            {isReading && (
              <button
                onClick={onStop}
                aria-label="Stop"
                className="w-9 h-9 rounded-full flex items-center justify-center transition active:scale-95"
                style={{
                  background: 'transparent',
                  color: INK_MID,
                  border: `1px solid ${LINE}`,
                }}
              >
                <Square size={12} strokeWidth={2} fill={INK_MID} />
              </button>
            )}

            {finished && (
              <button
                onClick={onStart}
                aria-label="Restart"
                className="ff-ui inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full transition active:scale-95"
                style={{
                  background: 'transparent',
                  color: GOLD_DEEP,
                  border: `1px solid ${ACCENT}`,
                  fontSize: 11,
                  fontWeight: 500,
                }}
              >
                <Play size={11} strokeWidth={2} />
                Replay
              </button>
            )}
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-3">
          <div
            className="h-[3px] w-full rounded-full overflow-hidden"
            style={{ background: 'rgba(166,124,58,0.15)' }}
          >
            <div
              className="h-full rounded-full"
              style={{
                width: `${Math.max(0, Math.min(100, progress))}%`,
                background: ACCENT,
                transition: 'width 480ms cubic-bezier(0.2,0.8,0.2,1)',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// Local styles
// =====================================================================
function DayStyles() {
  return (
    <style>{`
      .drop-cap::first-letter {
        font-family: 'Fraunces', Georgia, serif;
        font-weight: 500;
        font-size: 3.4em;
        line-height: 0.85;
        float: left;
        padding: 0.08em 0.12em 0 0;
        color: ${ACCENT};
        font-style: normal;
      }

      @keyframes typingDot {
        0%, 60%, 100% { opacity: 0.25; transform: translateY(0); }
        30% { opacity: 1; transform: translateY(-3px); }
      }
      .typing-dot { animation: typingDot 1.2s ease-in-out infinite; }
      .typing-dot:nth-child(2) { animation-delay: 0.18s; }
      .typing-dot:nth-child(3) { animation-delay: 0.36s; }

      @keyframes msgIn {
        from { opacity: 0; transform: translateY(6px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .msg-in { animation: msgIn 380ms cubic-bezier(0.2,0.8,0.2,1); }

      @keyframes sheetIn {
        from { opacity: 0; transform: translateY(20px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .sheet-in { animation: sheetIn 320ms cubic-bezier(0.2,0.8,0.2,1); }
    `}</style>
  );
}

// =====================================================================
// Daily check-in sheet — mirrors Intake (mode selector → text/voice)
// =====================================================================
function buildCheckinClosingLine(dayNumber) {
  if (dayNumber && dayNumber < 5) {
    return `Thank you. I heard everything you shared. I'll carry that into Day ${dayNumber + 1}.`;
  }
  return `Thank you. I heard everything you shared. I'll carry that into what comes next.`;
}

function CheckinSheet({
  dayNumber,
  dayContent,
  onClose,
  onComplete,
  guide,
  checkinAudioRef, // dormant — Day 7 removes
  voice,
}) {
  // -------- Conversation state --------
  // role uses the local guide/user vocabulary; we map to assistant/user
  // when calling conductDailyCheckin.
  const [checkinMessages, setCheckinMessages] = useState([]);
  const [isCheckinGuideSpeak, setIsCheckinGuideSpeak] = useState(false);
  const [isCheckinTyping, setIsCheckinTyping] = useState(false);
  const [checkinComplete, setCheckinComplete] = useState(false);
  const [checkinError, setCheckinError] = useState(null);
  const additionalContextRef = useRef(null);
  const hasStartedRef = useRef(false);
  const currentGuideAudioRef = useRef(null); // dormant — Day 7 removes

  // Mirror voice.state → isCheckinGuideSpeak. The hook is the source of
  // truth; the legacy setIsCheckinGuideSpeak setter calls inside
  // sendCheckinResponse + the opening-line effect still run but get
  // re-overridden by this effect within a render tick. Do NOT remove
  // those setters or this mirror — they coexist intentionally so the
  // dormant code paths in this file (speakCheckinMessage, etc.) stay
  // type-correct for Day 7 deletion.
  useEffect(() => {
    setIsCheckinGuideSpeak(voice.state === 'speaking');
  }, [voice.state]);

  // Detect transcribing → unlocked transition. The hook's onTurnComplete
  // fires on this transition; surfacing the "I heard …" confirm card
  // here keeps the routing self-contained inside CheckinSheet without
  // requiring a ref-bridge through DayDetail's onTurnComplete callback.
  const prevVoiceStateRef = useRef(voice.state);
  useEffect(() => {
    if (
      prevVoiceStateRef.current === 'transcribing' &&
      voice.state === 'unlocked'
    ) {
      setInlineCheckinShowConfirm(true);
    }
    prevVoiceStateRef.current = voice.state;
  }, [voice.state]);

  // -------- Inline tap-to-record (mirrors Intake.jsx, prefixed `checkin`) --------
  const inlineCheckinMediaRecorderRef = useRef(null);
  const inlineCheckinStreamRef = useRef(null);
  const inlineCheckinChunksRef = useRef([]);
  const inlineCheckinIsRecordingRef = useRef(false);
  const inlineCheckinButtonRef = useRef(null);
  const inlineCheckinStartRef = useRef(null);
  const inlineCheckinStopRef = useRef(null);
  const [inlineCheckinStatus, setInlineCheckinStatus] = useState('Tap to speak');
  const [inlineCheckinTranscript, setInlineCheckinTranscript] = useState('');
  const [inlineCheckinShowConfirm, setInlineCheckinShowConfirm] = useState(false);

  // -------- Speak helper --------
  // Resolves on the actual end of playback (via speakWithElevenLabs's
  // onEnd callback), not when play() resolves. The Promise from
  // speakWithElevenLabs settles when playback STARTS, so awaiting it
  // would advance the conversation while the guide was still talking.
  //
  // Three resolution paths feed safeResolve, gated by an idempotency
  // flag so only the first wins:
  //   1. onEnd callback (natural end / fallback / error inside elevenlabs)
  //   2. null return from speakWithElevenLabs (the call failed early)
  //   3. word-count safety timer (350ms/word + 8s) as final backstop
  //
  // Re-warms the iOS audio session before every utterance — Whisper's
  // getUserMedia release silently kills the output session even though
  // AudioContext.state still reads 'running'.
  const speakCheckinMessage = useCallback(
    (text) => {
      const wordCount = (text || '').trim().split(/\s+/).filter(Boolean).length;
      console.log('[Checkin] speakCheckinMessage START', {
        len: text?.length || 0,
        words: wordCount,
      });

      return new Promise((resolve) => {
        (async () => {
          let resolved = false;
          const safeResolve = (reason) => {
            if (resolved) return;
            resolved = true;
            console.log('[Checkin] speakCheckinMessage RESOLVED', {
              reason,
            });
            resolve();
          };

          if (!text) {
            safeResolve('no-text');
            return;
          }

          // Path A — persistent gesture-unlocked element exists. Reuse
          // it via .src swap so iOS doesn't see a fresh element on
          // turn 2+. This is the path that fixes Q3 silence.
          if (checkinAudioRef?.current) {
            if (isIOS) {
              try {
                await keepAudioSessionAlive();
                console.log(
                  '[Checkin] session kept alive, ctx state:',
                  getAudioSessionState()
                );
                // Shorter wait than the legacy path — the element is
                // already unlocked, we just need the session warm.
                await new Promise((r) => setTimeout(r, 400));
              } catch (e) {
                console.warn('[Checkin] keepalive error:', e?.message);
              }
            }

            try {
              console.log(
                '[Checkin] calling speakIntoElement (persistent path)'
              );
              await speakIntoElement(
                text,
                guide?.gender || 'female',
                checkinAudioRef.current,
                () => safeResolve('onEnd')
              );
              const safetyMs = wordCount * 350 + 8000;
              setTimeout(() => safeResolve('safety-timer'), safetyMs);
            } catch (e) {
              console.warn(
                '[Checkin] speakIntoElement error:',
                e?.message
              );
              safeResolve('error');
            }
            return;
          }

          // Path B (fallback) — no persistent element (handler didn't
          // run, or non-iOS / non-CheckinSheet caller). Use the
          // legacy speakWithElevenLabs path with a fresh Audio per
          // utterance. Same logging shape so traces are comparable.
          console.warn(
            '[Checkin] no persistent element — falling back to legacy speakWithElevenLabs'
          );
          if (currentGuideAudioRef.current) {
            try {
              stopElevenLabsAudio(currentGuideAudioRef.current);
            } catch {}
            currentGuideAudioRef.current = null;
          }
          if (isIOS) {
            try {
              await keepAudioSessionAlive();
              console.log(
                '[Checkin] session kept alive, ctx state:',
                getAudioSessionState()
              );
              await new Promise((r) => setTimeout(r, 800));
            } catch (e) {
              console.warn('[Checkin] keepalive error:', e?.message);
            }
          }
          try {
            console.log('[Checkin] calling speakWithElevenLabs (legacy)');
            const audio = await speakWithElevenLabs(
              text,
              guide?.gender || 'female',
              () => {
                currentGuideAudioRef.current = null;
                safeResolve('onEnd');
              }
            );
            console.log(
              '[Checkin] speakWithElevenLabs returned:',
              audio ? 'audio element' : 'null'
            );
            if (!audio) {
              safeResolve('null-audio');
              return;
            }
            currentGuideAudioRef.current = audio;
            const safetyMs = wordCount * 350 + 8000;
            setTimeout(() => safeResolve('safety-timer'), safetyMs);
          } catch (e) {
            console.warn(
              '[Checkin] speakCheckinMessage caught error:',
              e?.message
            );
            safeResolve('error');
          }
        })();
      });
    },
    [guide, checkinAudioRef]
  );

  // -------- Open the conversation: speak the opening question --------
  // Path B's AudioSessionManager already owns the audio session (claimed
  // by the parent's "Done & Check-In" tap via voice.init()), so the
  // legacy keepAudioSessionAlive belt-and-suspenders block is gone.
  useEffect(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;
    (async () => {
      const opening =
        "How did today land for you? Was there anything that felt especially personal — or anything that missed the mark? The more honest you are, the better I can shape what comes next.";
      setCheckinMessages([{ role: 'guide', content: opening }]);
      setIsCheckinGuideSpeak(true);
      try {
        await voice.speak(opening);
      } catch (e) {
        console.warn('[Checkin] opening speak failed:', e);
      }
      setIsCheckinGuideSpeak(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------- Send the user's reply to the guide --------
  async function sendCheckinResponse(text) {
    if (!text || !text.trim()) return;
    const trimmed = text.trim();
    const newMessages = [
      ...checkinMessages,
      { role: 'user', content: trimmed },
    ];
    setCheckinMessages(newMessages);
    setIsCheckinTyping(true);
    setCheckinError(null);

    try {
      const apiMessages = newMessages.map((m) => ({
        role: m.role === 'guide' ? 'assistant' : 'user',
        content: m.content,
      }));
      const { reply, isComplete, additionalContext } =
        await conductDailyCheckin(dayNumber, dayContent, apiMessages, guide);

      setIsCheckinTyping(false);
      if (reply) {
        setCheckinMessages((prev) => [
          ...prev,
          { role: 'guide', content: reply },
        ]);
        setIsCheckinGuideSpeak(true);
        try {
          await voice.speak(reply);
        } catch (e) {
          console.warn('[Checkin] reply speak failed:', e);
        }
        setIsCheckinGuideSpeak(false);
      }

      if (isComplete) {
        if (additionalContext) {
          additionalContextRef.current = additionalContext;
        }
        const closing = buildCheckinClosingLine(dayNumber);
        setCheckinMessages((prev) => [
          ...prev,
          { role: 'guide', content: closing },
        ]);
        setIsCheckinGuideSpeak(true);
        try {
          await voice.speak(closing);
        } catch (e) {
          console.warn('[Checkin] closing speak failed:', e);
        }
        setIsCheckinGuideSpeak(false);
        setCheckinComplete(true);
      }
    } catch (err) {
      console.error('[Checkin] sendCheckinResponse failed:', err);
      setIsCheckinTyping(false);
      setCheckinError(
        err?.message ||
          'Something went wrong. Try again or close this for now.'
      );
    }
  }

  // -------- Inline recorder (copied verbatim from Intake.jsx, prefixed) --------
  function inlineCheckinReleaseStream() {
    if (inlineCheckinStreamRef.current) {
      try {
        inlineCheckinStreamRef.current.getTracks().forEach((t) => t.stop());
      } catch {}
      inlineCheckinStreamRef.current = null;
    }
  }

  async function inlineCheckinStartRecording() {
    inlineCheckinChunksRef.current = [];
    try {
      const isIOSDevice = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      let stream = null;

      if (isIOSDevice) {
        if (inlineCheckinStreamRef.current) {
          try {
            inlineCheckinStreamRef.current
              .getTracks()
              .forEach((t) => t.stop());
          } catch {}
          inlineCheckinStreamRef.current = null;
        }
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        inlineCheckinStreamRef.current = stream;
      } else {
        const existing = inlineCheckinStreamRef.current;
        const isAlive =
          existing &&
          existing.active &&
          existing.getTracks().length > 0 &&
          existing
            .getTracks()
            .every((t) => t.readyState === 'live' && t.enabled);
        if (isAlive) {
          stream = existing;
        } else {
          if (existing) {
            try {
              existing.getTracks().forEach((t) => t.stop());
            } catch {}
          }
          setInlineCheckinStatus('Requesting mic...');
          stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          inlineCheckinStreamRef.current = stream;
        }
      }

      if (
        inlineCheckinMediaRecorderRef.current &&
        inlineCheckinMediaRecorderRef.current.state !== 'inactive'
      ) {
        try {
          inlineCheckinMediaRecorderRef.current.stop();
        } catch {}
      }

      const mimeType = isIOSDevice
        ? 'audio/mp4'
        : MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : '';

      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      inlineCheckinMediaRecorderRef.current = mr;

      mr.ondataavailable = (e) => {
        if (e.data.size > 0) inlineCheckinChunksRef.current.push(e.data);
      };

      mr.onstart = () => {
        inlineCheckinIsRecordingRef.current = true;
        setInlineCheckinStatus('Listening… tap to send');
        if (inlineCheckinButtonRef.current) {
          inlineCheckinButtonRef.current.style.background = '#8B6914';
          inlineCheckinButtonRef.current.style.color = '#F2EBDC';
          inlineCheckinButtonRef.current.style.boxShadow =
            '0 0 0 12px rgba(139,105,20,0.25), 0 0 0 24px rgba(139,105,20,0.10)';
          const span = inlineCheckinButtonRef.current.querySelector('span');
          if (span) span.textContent = 'TAP TO STOP';
          const svg = inlineCheckinButtonRef.current.querySelector('svg');
          if (svg) svg.setAttribute('stroke', '#F2EBDC');
        }
      };

      mr.onerror = (e) => {
        console.error('[Checkin/Inline] MediaRecorder error:', e?.error || e);
        inlineCheckinIsRecordingRef.current = false;
        setInlineCheckinStatus('Error — tap to try again');
        inlineCheckinReleaseStream();
      };

      mr.start(1000);
    } catch (err) {
      console.error(
        '[Checkin/Inline] startRecording failed:',
        err.name,
        err.message
      );
      setInlineCheckinStatus('Mic error — tap to try again');
      inlineCheckinIsRecordingRef.current = false;
      inlineCheckinReleaseStream();
    }
  }

  async function inlineCheckinStopRecording() {
    inlineCheckinIsRecordingRef.current = false;

    if (inlineCheckinButtonRef.current) {
      inlineCheckinButtonRef.current.style.background = '#F2EBDC';
      inlineCheckinButtonRef.current.style.color = '#A67C3A';
      inlineCheckinButtonRef.current.style.boxShadow = 'none';
      const span = inlineCheckinButtonRef.current.querySelector('span');
      if (span) span.textContent = 'TAP';
      const svg = inlineCheckinButtonRef.current.querySelector('svg');
      if (svg) svg.setAttribute('stroke', '#A67C3A');
    }

    if (
      !inlineCheckinMediaRecorderRef.current ||
      inlineCheckinMediaRecorderRef.current.state === 'inactive'
    ) {
      setInlineCheckinStatus('Tap to speak');
      return;
    }

    setInlineCheckinStatus('Transcribing...');

    inlineCheckinMediaRecorderRef.current.onstop = async () => {
      const isIOSDevice = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      if (isIOSDevice && inlineCheckinStreamRef.current) {
        try {
          inlineCheckinStreamRef.current.getTracks().forEach((t) => t.stop());
        } catch {}
        inlineCheckinStreamRef.current = null;
      }

      const mimeType =
        inlineCheckinMediaRecorderRef.current?.mimeType || 'audio/mp4';
      const blob = new Blob(inlineCheckinChunksRef.current, {
        type: mimeType,
      });

      if (blob.size < 500) {
        setInlineCheckinStatus('Too short — try again');
        return;
      }

      try {
        const text = await transcribeAudio(blob);
        if (text && text.trim()) {
          setInlineCheckinTranscript(text.trim());
          setInlineCheckinShowConfirm(true);
          setInlineCheckinStatus('Tap to speak');
        } else {
          setInlineCheckinStatus('Nothing heard — try again');
        }
      } catch (err) {
        console.error('[Checkin/Inline] Whisper error:', err);
        setInlineCheckinStatus('Error — try again');
      }
    };

    try {
      inlineCheckinMediaRecorderRef.current.requestData();
    } catch {}

    await new Promise((r) => setTimeout(r, 300));
    inlineCheckinMediaRecorderRef.current.stop();
  }

  inlineCheckinStartRef.current = inlineCheckinStartRecording;
  inlineCheckinStopRef.current = inlineCheckinStopRecording;

  // Latest-version ref so the native event-listener closure (created
  // once on mount) always reaches the freshest hook state + methods.
  const voiceRef = useRef(voice);
  useEffect(() => {
    voiceRef.current = voice;
  });

  const inlineCheckinButtonCallbackRef = useCallback((btn) => {
    if (!btn) return;
    inlineCheckinButtonRef.current = btn;

    async function handleTap(e) {
      if (e.cancelable) e.preventDefault();
      e.stopPropagation();
      const v = voiceRef.current;
      if (!v) return;
      try {
        if (v.state === 'listening') {
          await v.stopListening();
        } else {
          await v.startListening();
        }
      } catch (err) {
        console.warn('[Checkin/Inline] mic toggle threw:', err?.message);
      }
    }

    btn.addEventListener('touchend', handleTap, { passive: false });
    btn.addEventListener('click', handleTap);
  }, []);

  // Cleanup on unmount. The dormant currentGuideAudioRef +
  // inlineCheckin* recorder teardown calls stay as belt-and-suspenders;
  // the new pipeline owns its own teardown via voice.cancel(), but the
  // legacy refs are still declared and Day 7 deletes the lot.
  useEffect(() => {
    return () => {
      try {
        voice.cancel();
      } catch {}
      if (currentGuideAudioRef.current) {
        try {
          stopElevenLabsAudio(currentGuideAudioRef.current);
        } catch {}
        currentGuideAudioRef.current = null;
      }
      if (
        inlineCheckinMediaRecorderRef.current &&
        inlineCheckinMediaRecorderRef.current.state !== 'inactive'
      ) {
        try {
          inlineCheckinMediaRecorderRef.current.stop();
        } catch {}
      }
      inlineCheckinReleaseStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleDone() {
    try {
      voice.cancel();
    } catch {}
    inlineCheckinReleaseStream();
    onComplete(additionalContextRef.current || null);
  }

  function handleClose() {
    try {
      voice.cancel();
    } catch {}
    inlineCheckinReleaseStream();
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center"
      style={{ background: 'rgba(27,24,20,0.55)' }}
      onClick={handleClose}
    >
      <div
        className="w-full max-w-[460px] flex flex-col"
        style={{
          background: PAPER,
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          maxHeight: '92vh',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Sheet header */}
        <div
          className="flex items-center justify-between px-5 pt-4 pb-3"
          style={{ borderBottom: `1px solid ${LINE}` }}
        >
          <div className="flex items-center gap-2">
            <FoxMark size={22} variant="gold" />
            <div>
              <div
                className="ff-mono"
                style={{
                  color: INK_SOFT,
                  fontSize: 9,
                  letterSpacing: '0.18em',
                }}
              >
                CHECK-IN · DAY {dayNumber}
              </div>
              <div
                className="ff-display"
                style={{ color: INK, fontSize: 14, fontWeight: 500 }}
              >
                {guide?.name || 'Your guide'}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 -mr-2 rounded-full transition active:scale-95"
            aria-label="Close check-in"
          >
            <X size={20} strokeWidth={1.5} style={{ color: INK_MID }} />
          </button>
        </div>

        {/* Conversation transcript */}
        <div
          className="flex-1 overflow-y-auto px-5 py-4"
          style={{ minHeight: 200 }}
        >
          {checkinMessages.map((m, i) => (
            <Fragment key={i}>
              <div
                style={{
                  display: 'flex',
                  justifyContent:
                    m.role === 'user' ? 'flex-end' : 'flex-start',
                  marginBottom: 12,
                }}
              >
                <div
                  style={{
                    maxWidth: '85%',
                    background:
                      m.role === 'user' ? 'rgba(166,124,58,0.12)' : 'transparent',
                    border:
                      m.role === 'user'
                        ? '1px solid rgba(166,124,58,0.25)'
                        : 'none',
                    borderRadius: 12,
                    padding: m.role === 'user' ? '10px 14px' : '4px 0',
                    fontSize: 14.5,
                    fontFamily: 'Georgia, serif',
                    color: INK,
                    lineHeight: 1.6,
                    fontStyle: m.role === 'guide' ? 'italic' : 'normal',
                  }}
                >
                  {m.content}
                </div>
              </div>
              {m.role === 'guide' && detectCrisis(m.content) && <CrisisResourceCard />}
            </Fragment>
          ))}

          {isCheckinGuideSpeak && (
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <div
                style={{
                  display: 'flex',
                  gap: 6,
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginBottom: 6,
                }}
              >
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: ACCENT,
                      animation: `typing-dot 1.2s ease-in-out ${i * 0.4}s infinite`,
                    }}
                  />
                ))}
              </div>
              <p
                style={{
                  fontFamily: 'Georgia, serif',
                  fontStyle: 'italic',
                  fontSize: 12,
                  color: INK,
                  opacity: 0.5,
                  margin: 0,
                }}
              >
                {guide?.name || 'Your guide'} is speaking…
              </p>
            </div>
          )}

          {isCheckinTyping && (
            <div style={{ textAlign: 'center', padding: 16 }}>
              <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: ACCENT,
                      animation: `typing-dot 1.2s ease-in-out ${i * 0.4}s infinite`,
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {checkinError && (
            <div
              style={{
                margin: '8px 0',
                padding: '10px 14px',
                background: 'rgba(91,42,46,0.08)',
                border: '1px solid rgba(91,42,46,0.25)',
                color: '#5B2A2E',
                borderRadius: 10,
                fontSize: 12,
                textAlign: 'center',
              }}
            >
              {checkinError}
            </div>
          )}
        </div>

        {/* Recording / confirm / done */}
        <div style={{ padding: '12px 16px 24px' }}>
          {!checkinComplete &&
            !isCheckinGuideSpeak &&
            !isCheckinTyping &&
            !inlineCheckinShowConfirm && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 0',
                }}
              >
                <button
                  ref={inlineCheckinButtonCallbackRef}
                  type="button"
                  style={{
                    width: 120,
                    height: 120,
                    borderRadius: '50%',
                    background:
                      voice.state === 'listening' ? '#8B6914' : PAPER,
                    border: `3px solid ${ACCENT}`,
                    color: voice.state === 'listening' ? PAPER : ACCENT,
                    fontSize: 11,
                    fontWeight: 700,
                    fontFamily: 'Arial, sans-serif',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    WebkitTapHighlightColor: 'transparent',
                    touchAction: 'none',
                    userSelect: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    boxShadow:
                      voice.state === 'listening'
                        ? '0 0 0 12px rgba(139,105,20,0.25), 0 0 0 24px rgba(139,105,20,0.10)'
                        : 'none',
                    transition:
                      'background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease',
                  }}
                >
                  <svg
                    width="36"
                    height="36"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                    <line x1="12" y1="19" x2="12" y2="23" />
                    <line x1="8" y1="23" x2="16" y2="23" />
                  </svg>
                  <span>
                    {voice.state === 'listening' ? 'TAP TO STOP' : 'TAP'}
                  </span>
                </button>
                <p
                  style={{
                    fontSize: 12,
                    color: INK,
                    opacity: 0.45,
                    fontFamily: 'Arial, sans-serif',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    margin: 0,
                  }}
                >
                  {voice.state === 'listening'
                    ? 'Listening… tap to send'
                    : voice.state === 'transcribing'
                    ? 'Transcribing…'
                    : 'Tap to speak'}
                </p>
              </div>
            )}

          {/* "I heard …" confirm card. Bound to voice.currentTranscript;
              inlineCheckinShowConfirm is flipped true by the transcribing
              → unlocked transition effect above, and reset on Send /
              Try again. */}
          {!checkinComplete &&
            inlineCheckinShowConfirm &&
            voice.currentTranscript && (
              <div>
                <div
                  style={{
                    background: PAPER,
                    border: '1px solid rgba(166,124,58,0.3)',
                    borderRadius: 12,
                    padding: '12px 16px',
                    marginBottom: 10,
                  }}
                >
                  <p
                    style={{
                      fontSize: 11,
                      color: ACCENT,
                      fontFamily: 'monospace',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      margin: '0 0 6px',
                    }}
                  >
                    I heard
                  </p>
                  <p
                    style={{
                      fontSize: 15,
                      color: INK,
                      fontFamily: 'Georgia, serif',
                      fontStyle: 'italic',
                      margin: 0,
                      lineHeight: 1.5,
                    }}
                  >
                    &ldquo;{voice.currentTranscript}&rdquo;
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => {
                      // Dismiss the card. The next listen → transcribe
                      // cycle overwrites voice.currentTranscript on its
                      // own, so no explicit clear is needed.
                      setInlineCheckinShowConfirm(false);
                    }}
                    style={{
                      flex: 1,
                      padding: 12,
                      borderRadius: 10,
                      background: 'transparent',
                      border: `1.5px solid ${ACCENT}`,
                      color: ACCENT,
                      fontSize: 14,
                      cursor: 'pointer',
                      fontWeight: 600,
                    }}
                  >
                    Try again
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const t = voice.currentTranscript;
                      setInlineCheckinShowConfirm(false);
                      sendCheckinResponse(t);
                    }}
                    style={{
                      flex: 2,
                      padding: 12,
                      borderRadius: 10,
                      background: INK,
                      border: 'none',
                      color: PAPER,
                      fontSize: 14,
                      cursor: 'pointer',
                      fontWeight: 600,
                    }}
                  >
                    Send
                  </button>
                </div>
              </div>
            )}

          {checkinComplete && (
            <div style={{ textAlign: 'center', padding: '20px 24px' }}>
              <ReactiveFoxMark
                amplitude={0}
                isActive={false}
                size={52}
                style={{ margin: '0 auto 16px' }}
              />

              <p
                style={{
                  fontFamily: 'Georgia, serif',
                  fontSize: 16,
                  color: INK,
                  opacity: 0.7,
                  fontStyle: 'italic',
                  lineHeight: 1.7,
                  marginBottom: 24,
                }}
              >
                {guide?.name || 'Your guide'} has noted everything you shared
                and will carry it into tomorrow.
              </p>

              {/* Share prompt — only on Day 1 */}
              {dayNumber === 1 && (
                <div
                  style={{
                    background: 'rgba(166,124,58,0.06)',
                    border: '1px solid rgba(166,124,58,0.15)',
                    borderRadius: 12,
                    padding: '16px 20px',
                    marginBottom: 20,
                    textAlign: 'center',
                  }}
                >
                  <p
                    style={{
                      fontSize: 14,
                      color: INK,
                      fontFamily: 'Georgia, serif',
                      fontStyle: 'italic',
                      lineHeight: 1.6,
                      marginBottom: 12,
                      opacity: 0.7,
                    }}
                  >
                    If today helped you, share Rooted with one person who might
                    need it.
                  </p>
                  <button
                    type="button"
                    onClick={async () => {
                      const shareText = `I just finished my first day of Rooted — a personalized Bible study built around who I actually am. It is unlike anything I have tried. Check it out: app.goldenfoxpublishing.com`;
                      if (navigator.share) {
                        try {
                          await navigator.share({
                            text: shareText,
                            url: 'https://app.goldenfoxpublishing.com',
                          });
                        } catch (err) {
                          if (err.name !== 'AbortError') {
                            navigator.clipboard?.writeText(shareText);
                          }
                        }
                      } else {
                        navigator.clipboard?.writeText(shareText);
                        alert('Copied to clipboard!');
                      }
                    }}
                    style={{
                      background: INK,
                      color: PAPER,
                      border: 'none',
                      borderRadius: 10,
                      padding: '12px 24px',
                      fontSize: 14,
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontFamily: 'Georgia, serif',
                      width: '100%',
                    }}
                  >
                    Share Rooted →
                  </button>
                </div>
              )}

              <button
                type="button"
                onClick={handleDone}
                style={{
                  background: 'transparent',
                  color: INK,
                  border: '1px solid rgba(27,24,20,0.2)',
                  borderRadius: 10,
                  padding: '12px 24px',
                  fontSize: 14,
                  cursor: 'pointer',
                  fontFamily: 'Georgia, serif',
                  width: '100%',
                  opacity: 0.6,
                }}
              >
                Done — back to my week
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// MAIN — DayDetail
// =====================================================================
export default function DayDetail() {
  const navigate = useNavigate();
  const { dayNumber: dayParam, weekNumber: weekParam } = useParams();
  const dayNum = parseInt(dayParam, 10) || 1;
  const weekNum = weekParam ? parseInt(weekParam, 10) : 1;
  const idx = Math.max(0, Math.min(dayNum - 1, 4));

  const {
    study,
    weeks,
    getWeek,
    addWeek,
    intakeMessages,
    dailyFeedback,
    userProfile,
    guide,
    appendDailyFeedback,
    addHighlight,
    getHighlights,
    addJournalEntry,
    getJournalEntries,
    markDayComplete,
    isDayComplete,
    journalEntries,
  } = useApp();
  const dayIsComplete = isDayComplete(dayNum, weekNum);
  const weekData = getWeek(weekNum) || (weekNum === 1 ? study : null);
  const { isActive: isPayingOrTrialing, isLoading: isSubLoading } =
    useSubscription();

  // Clerk session token for the voice-conversation hook's repo to sync
  // check-in turns to Supabase. DayDetail is post-auth (wrapped in
  // ProtectedRoute), so getToken() returns a valid JWT. Refresh every
  // 50s — Clerk JWTs default to a ~60s lifetime.
  const { getToken } = useAuth();
  const [authToken, setAuthToken] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const fetchToken = async () => {
      try {
        const t = await getToken();
        if (!cancelled) setAuthToken(t || null);
      } catch {
        if (!cancelled) setAuthToken(null);
      }
    };
    fetchToken();
    const interval = setInterval(fetchToken, 50_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [getToken]);

  // Lifted hook (Option 2): one IntakeRepository + RetryQueue per page
  // navigation, not per check-in open. sessionId stays null so the hook
  // auto-generates a UUID on the first saveTurn. KNOWN LIMITATION: closing
  // and reopening the check-in sheet inside the same DayDetail visit
  // reuses the same session_id — Day 7 polish adds voice.resetSession()
  // to clear the internal sessionIdRef between sheet opens. Persistence
  // writes to intake_messages (Day 3 schema) with blank metadata; Day 7
  // polish adds defaultMetadata: { kind: 'checkin', day, week } to the
  // hook constructor.
  const voice = useVoiceConversation({
    userId: null,
    authToken,
    sessionId: null,
    voiceId: VOICE_IDS[guide?.gender] || VOICE_IDS.female,
    onTurnComplete: () => {
      // No-op here — CheckinSheet detects the transcribing → unlocked
      // transition internally via a voice.state effect and surfaces the
      // "I heard …" confirm card without a ref-bridge.
    },
    onError: (err) => {
      console.error('[DayDetail/voice]', err);
    },
  });

  // Highlight + Journal state
  const [highlightMode, setHighlightMode] = useState(false);
  const [highlightToast, setHighlightToast] = useState(false);
  const [showJournal, setShowJournal] = useState(false);

  const [showCheckin, setShowCheckin] = useState(false);

  // Reading-out-loud state
  const [isReading, setIsReading] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentChunkIndex, setCurrentChunkIndex] = useState(0);
  const [currentChunkLabel, setCurrentChunkLabel] = useState('');
  const [readingProgress, setReadingProgress] = useState(0);
  const [readingFinished, setReadingFinished] = useState(false);
  const [readingAmplitude, setReadingAmplitude] = useState(0);
  const readingQueueRef = useRef([]);
  const readingIndexRef = useRef(0);
  const currentAudioRef = useRef(null);
  // Single persistent Audio element. Created once on mount and reused
  // for every chunk. iOS Safari only unlocks an HTMLMediaElement during
  // the user gesture that triggered its first play() — making a fresh
  // `new Audio()` per chunk meant chunks 2..N were never unlocked, so
  // play() resolved silently and onended never fired. Reusing the same
  // element keeps the iOS audio session live across the whole reading.
  const audioElementRef = useRef(null);
  // Persistent audio element for the check-in conversation. Created
  // and gesture-unlocked synchronously inside handleDoneAndCheckin so
  // every utterance in the conversation reuses the same unlocked
  // element via .src swaps.
  const checkinAudioRef = useRef(null);
  const isPausedRef = useRef(false);
  const isReadingRef = useRef(false);
  const amplitudeRafRef = useRef(null);
  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);
  useEffect(() => {
    isReadingRef.current = isReading;
  }, [isReading]);

  // Mount the single Audio element. playsInline keeps iOS from kicking
  // playback into a fullscreen video player once a real src is assigned.
  useEffect(() => {
    const audio = new Audio();
    audio.playsInline = true;
    audioElementRef.current = audio;
    return () => {
      if (audioElementRef.current) {
        try {
          audioElementRef.current.pause();
          audioElementRef.current.src = '';
        } catch {}
      }
    };
  }, []);

  // Pre-warm the iOS audio session 500ms after mount. The first
  // resume() will likely be rejected without a gesture — the inner
  // catch in keepAudioSessionAlive swallows it — but creating the
  // AudioContext early shaves work off the critical path of the next
  // user gesture (Listen tap or Done & Check In). Releases the
  // session on unmount so navigating away cleans up the oscillator.
  useEffect(() => {
    if (isIOS) {
      const timer = setTimeout(async () => {
        try {
          await keepAudioSessionAlive();
          console.log('[DayDetail] Audio session pre-warmed on mount');
        } catch {}
      }, 500);
      return () => {
        clearTimeout(timer);
        releaseAudioSession();
      };
    }
    return () => releaseAudioSession();
  }, []);

  // Drive readingAmplitude from the analyser while we're actively reading.
  useEffect(() => {
    if (!isReading || isPaused) {
      if (amplitudeRafRef.current) cancelAnimationFrame(amplitudeRafRef.current);
      amplitudeRafRef.current = null;
      setReadingAmplitude(0);
      return;
    }
    let alive = true;
    const tick = () => {
      if (!alive) return;
      setReadingAmplitude(getCurrentAmplitude());
      amplitudeRafRef.current = requestAnimationFrame(tick);
    };
    tick();
    return () => {
      alive = false;
      if (amplitudeRafRef.current) cancelAnimationFrame(amplitudeRafRef.current);
      amplitudeRafRef.current = null;
    };
  }, [isReading, isPaused]);

  // Stop reading on unmount / navigate-away
  useEffect(() => {
    return () => {
      if (currentAudioRef.current) {
        stopElevenLabsAudio(currentAudioRef.current);
        currentAudioRef.current = null;
      }
      isReadingRef.current = false;
      isPausedRef.current = false;
    };
  }, []);

  // Paywall gate — Week 1 (Days 1-5) is free forever for non-payers.
  // Anything past Week 1 (i.e. "Day 6+" in linear terms — this codebase
  // tracks 1-5 within each week, so the spec's Day 6+ maps to weekNum
  // >= 2) requires an active or trialing subscription. The !isSubLoading
  // guard avoids a paywall flash while useSubscription is still
  // resolving the Stripe lookup on first paint.
  if (weekNum >= 2 && !isPayingOrTrialing && !isSubLoading) {
    return <Navigate to="/paywall" replace />;
  }

  if (
    !weekData ||
    !Array.isArray(weekData.days) ||
    !weekData.days[idx]
  ) {
    // Returning users hit this branch briefly while Supabase data hydrates
    // on sign-in. Render a plain loading state so the read button (and the
    // rest of the screen) stays absent until day data is actually present.
    return (
      <div
        style={{
          minHeight: '100vh',
          background: PAPER,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <FontStyles />
        <p
          style={{
            fontFamily: 'Georgia, serif',
            color: INK,
            opacity: 0.4,
            fontStyle: 'italic',
          }}
        >
          Loading your study…
        </p>
      </div>
    );
  }

  const day = weekData.days[idx];
  const heroColor = DAY_COLORS[idx] || DAY_COLORS[0];
  const dow = day.day_name || DOW[idx] || `Day ${dayNum}`;

  // Highlights + journal lookups for THIS day
  const dayHighlights = getHighlights(dayNum);
  const dayJournalEntries = getJournalEntries(dayNum);
  const highlightCount = dayHighlights.length;
  const hasJournalForToday = dayJournalEntries.length > 0;
  // Set of highlighted strings for fast contains check during render
  const highlightTextSet = new Set(dayHighlights.map((h) => h.text));

  // While in highlightMode, capture the current selection inside the body
  // and persist it. Hides immediately after capture.
  const captureSelection = () => {
    if (!highlightMode) return;
    const sel = window.getSelection?.();
    if (!sel) return;
    const text = sel.toString().trim();
    if (!text || text.length < 3) return;
    if (highlightTextSet.has(text)) {
      sel.removeAllRanges();
      return;
    }
    addHighlight(dayNum, text);
    sel.removeAllRanges();
    setHighlightToast(true);
    setTimeout(() => setHighlightToast(false), 1400);
  };

  // -------------- Read-aloud controls --------------
  // Reuses the single persistent audioElementRef across every chunk.
  // iOS only unlocks the element it played first inside the user
  // gesture; creating a fresh `new Audio()` per chunk produced silent
  // playback after the first chunk because the new element was never
  // unlocked. Swapping `src` on the same element keeps the audio
  // session live and onended firing reliably.
  const playSingleChunk = (text, retryCount = 0) =>
    new Promise((resolve) => {
      (async () => {
        if (!text || !text.trim()) {
          resolve(true);
          return;
        }

        try {
          console.log('[Reading] Fetching chunk, length:', text.length);

          const response = await authenticatedFetch('/api/speak', {
            method: 'POST',
            body: JSON.stringify({
              text: text.trim(),
              voiceId: guide?.gender || 'female',
            }),
          });

          if (!response.ok) {
            console.warn('[Reading] API error:', response.status);
            // Retry once before giving up — transient ElevenLabs failures
            // were causing chunks to be skipped silently.
            if (retryCount === 0) {
              console.log('[Reading] Retrying chunk after 2s...');
              await new Promise((r) => setTimeout(r, 2000));
              const retryResult = await playSingleChunk(text, 1);
              resolve(retryResult);
            } else {
              resolve(false);
            }
            return;
          }

          const blob = await response.blob();
          const url = URL.createObjectURL(blob);

          const audio = audioElementRef.current;
          if (!audio) {
            console.warn('[Reading] No audio element — component unmounted?');
            try {
              URL.revokeObjectURL(url);
            } catch {}
            resolve(false);
            return;
          }

          // Clear any leftover legacy on* handlers from a prior chunk
          // before we wire up the once-listeners for this chunk.
          audio.onended = null;
          audio.onerror = null;
          audio.pause();
          audio.src = url;
          audio.load();

          // Keep currentAudioRef pointing at the persistent element so
          // any other code paths that still read it (cleanup effects,
          // legacy callsites) operate on the same instance.
          currentAudioRef.current = audio;

          // Very generous fallback — 350ms per word + 15s buffer.
          // The fallback only fires if ended/error never do, so it must
          // be longer than any plausible real playback duration.
          const words = text.trim().split(/\s+/).length;
          const fallbackMs = words * 350 + 15000;
          console.log(
            '[Reading] words:',
            words,
            'fallback:',
            fallbackMs + 'ms'
          );

          let resolved = false;

          const done = (reason) => {
            if (resolved) return;
            resolved = true;
            console.log('[Reading] chunk done via:', reason);
            clearTimeout(fallback);
            try {
              URL.revokeObjectURL(url);
            } catch {}
            resolve(true);
          };

          const fallback = setTimeout(() => done('fallback'), fallbackMs);

          audio.addEventListener('ended', () => done('ended'), { once: true });
          audio.addEventListener(
            'error',
            (e) => {
              console.warn('[Reading] audio error:', e);
              done('error');
            },
            { once: true }
          );

          try {
            await audio.play();
            console.log('[Reading] play() started');
          } catch (playErr) {
            console.warn('[Reading] play() failed:', playErr.message);
            done('play-error');
          }
        } catch (err) {
          console.error('[Reading] playSingleChunk error:', err);
          resolve(false);
        }
      })();
    });

  // Activate the persistent iOS audio session inside the listen tap
  // (no-op on non-iOS, and a no-op on iOS once the context is already
  // running). 600ms wait gives the engine time to fully accept
  // HTMLAudioElement playback before the first fetch kicks off — the
  // mount-time pre-warm has already done most of the cold-start work
  // so this is shorter than it used to be.
  const handleListenPress = async () => {
    if (isIOS) {
      try {
        await keepAudioSessionAlive();
        await new Promise((r) => setTimeout(r, 600));
        console.log(
          '[DayDetail] Audio session confirmed warm — starting reading'
        );
      } catch (e) {
        console.warn('[Reading] Session keeper failed:', e?.message);
      }
    }
    await startReading();
  };

  const startReading = async () => {
    if (!day) {
      console.warn('[Reading] No day data — cannot start reading');
      return;
    }

    const queue = buildReadingQueue(day, userProfile, guide);
    if (!queue.length) {
      console.warn('[Reading] Empty queue — no content to read');
      return;
    }


    console.log(
      '[Reading] Starting with',
      queue.length,
      'chunks:',
      queue.map((q) => q.label)
    );

    readingQueueRef.current = queue;
    readingIndexRef.current = 0;
    isReadingRef.current = true;
    isPausedRef.current = false;

    setReadingFinished(false);
    setIsReading(true);
    setIsPaused(false);
    setCurrentChunkIndex(0);
    setCurrentChunkLabel(queue[0]?.label || '');
    setReadingProgress(0);

    let i = 0;
    while (i < queue.length) {
      if (!isReadingRef.current) {
        console.log('[Reading] Stopped at chunk', i);
        break;
      }

      if (isPausedRef.current) {
        console.log('[Reading] Paused at chunk', i);
        readingIndexRef.current = i;
        await new Promise((resolve) => {
          const check = setInterval(() => {
            if (!isPausedRef.current || !isReadingRef.current) {
              clearInterval(check);
              resolve();
            }
          }, 300);
        });
        continue;
      }

      const chunk = queue[i];
      readingIndexRef.current = i;
      setCurrentChunkIndex(i);
      setCurrentChunkLabel(chunk.label);
      setReadingProgress(Math.round((i / queue.length) * 100));

      console.log(
        '[Reading] Chunk',
        i,
        '—',
        chunk.label,
        '— chars:',
        chunk.text?.length
      );

      const success = await playSingleChunk(chunk.text);
      console.log('[Reading] Chunk', i, 'done, success:', success);

      // Inter-chunk pause — iOS needs more time for the audio session to settle
      // before the next play() will start cleanly.
      const isIOSUA = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      const pauseBetweenChunks = isIOSUA ? 1200 : 600;

      // Skip the inter-chunk wait if the user has paused or stopped — no
      // sense burning 600-1200ms before the next iteration's pause-check
      // notices.
      if (!isPausedRef.current && isReadingRef.current) {
        await new Promise((r) => setTimeout(r, pauseBetweenChunks));
      }

      // If a pause landed during/after the inter-chunk wait, advance the
      // index now and block until the user resumes (or stops) so the
      // next chunk doesn't start while paused.
      if (isPausedRef.current && isReadingRef.current) {
        readingIndexRef.current = i + 1;
        await new Promise((resolve) => {
          const check = setInterval(() => {
            if (!isPausedRef.current || !isReadingRef.current) {
              clearInterval(check);
              resolve();
            }
          }, 300);
        });
      }

      i += 1;
    }

    if (isReadingRef.current) {
      console.log('[Reading] All chunks complete');
      setIsReading(false);
      isReadingRef.current = false;
      setCurrentChunkLabel('Done');
      setReadingProgress(100);
      setReadingFinished(true);
      currentAudioRef.current = null;
    }
  };

  const pauseReading = () => {
    console.log('[Reading] Pausing');
    isPausedRef.current = true;
    setIsPaused(true);
    if (audioElementRef.current) {
      try {
        audioElementRef.current.pause();
      } catch {}
    }
  };

  const resumeReading = () => {
    console.log('[Reading] Resuming');
    isPausedRef.current = false;
    setIsPaused(false);
    const audio = audioElementRef.current;
    if (audio && audio.src) {
      try {
        audio.play().catch(() => {
          // Resume failed (iOS quirk, or the chunk's fallback already
          // revoked the blob URL). The running loop's polling check
          // has already unblocked and will advance to the next chunk.
        });
      } catch {}
    }
  };

  const stopReading = () => {
    console.log('[Reading] Stopping');
    isReadingRef.current = false;
    isPausedRef.current = false;
    if (audioElementRef.current) {
      try {
        audioElementRef.current.pause();
        audioElementRef.current.src = '';
      } catch {}
    }
    currentAudioRef.current = null;
    setIsReading(false);
    setIsPaused(false);
    setCurrentChunkLabel('');
    setReadingProgress(0);
    setCurrentChunkIndex(0);
    setReadingFinished(false);
    readingIndexRef.current = 0;
  };

  // Bound to the "Done & Check In" buttons. Two pieces of work that
  // MUST run synchronously inside this tap so iOS sees them as
  // gesture-bound:
  //   1. Construct an Audio element with a tiny silent MP3 src and
  //      call .play() (fire-and-forget) — this unlocks the specific
  //      element so subsequent .src swaps work without fresh gestures.
  //   2. keepAudioSessionAlive() to start/refresh the persistent
  //      AudioContext + oscillator.
  // releaseAudioSession + cleanupCheckinAudio fire when the sheet
  // closes / completes.
  const cleanupCheckinAudio = () => {
    if (checkinAudioRef.current) {
      try {
        checkinAudioRef.current.pause();
      } catch {}
      try {
        checkinAudioRef.current.removeAttribute('src');
        checkinAudioRef.current.load();
      } catch {}
      checkinAudioRef.current = null;
    }
  };

  // Returning visit on an already-completed day. Just dismiss back to
  // the day list — no Whisper, no Anthropic, no TTS, no DB writes.
  // The original markDayComplete record from the first visit stays
  // as-is (handleCheckinComplete inserts only on first completion).
  const handleDoneNoCheckin = () => {
    stopReading();
    navigate('/study');
  };

  const handleDoneAndCheckin = async () => {
    // Safety guard: if a stale render or race fires this on a
    // completed day, route to the no-checkin path instead.
    if (dayIsComplete) {
      handleDoneNoCheckin();
      return;
    }

    // voice.init() MUST be the first audio call inside this gesture —
    // it creates the AudioContext and claims the iOS audio session for
    // Playback (via AudioSessionManager's persistent sink element +
    // ConstantSourceNode). Every later speak/listen ride that claimed
    // session, so no per-utterance gesture is needed.
    try {
      await voice.init();
    } catch (e) {
      console.warn('[Checkin] voice.init() threw:', e?.message);
    }

    stopReading();
    setShowCheckin(true);
  };

  const handleCheckinComplete = async (entry) => {
    cleanupCheckinAudio();
    // releaseAudioSession() dropped — Path B's persistent sink owns the
    // audio session for the page lifetime; releasing here could
    // plausibly interfere with the next session claim.

    if (entry && entry.dayNumber) {
      appendDailyFeedback({ ...entry, weekNumber: weekNum });
    }

    // Capture the full self-contained day record. This is the data
    // structure the future end-of-year book generator will read from.
    const dayRecord = weekData?.days?.[idx];
    const dayJournalEntry =
      journalEntries
        ?.filter(
          (e) =>
            e.dayNumber === dayNum && (e.weekNumber || 1) === weekNum
        )
        ?.map((e) => e.entry)
        ?.join('\n\n') || '';

    markDayComplete({
      dayNumber: dayNum,
      weekNumber: weekNum,
      completedAt: new Date().toISOString(),
      title: dayRecord?.title || '',
      subtitle: dayRecord?.subtitle || '',
      scripture_reference: dayRecord?.scripture_reference || '',
      scripture_text: dayRecord?.scripture_text || '',
      opening_prayer: dayRecord?.opening_prayer || '',
      content_sections: dayRecord?.content_sections || [],
      enneagram_insight: dayRecord?.enneagram_insight || '',
      pull_quote: dayRecord?.pull_quote || '',
      reflection_questions: dayRecord?.reflection_questions || [],
      closing_practice: dayRecord?.closing_practice || '',
      journalEntry: dayJournalEntry,
      checkinResponse: entry?.summary || entry?.raw || '',
      userProfile: {
        name: userProfile?.name,
        enneagram_type: userProfile?.enneagram_type,
        enneagram_wing: userProfile?.enneagram_wing,
        enneagram_label: userProfile?.enneagram_label,
      },
      guideName: guide?.name,
    });

    setShowCheckin(false);

    // Week 1 Day 5 + non-payer = end of the free trial. Push to the
    // paywall before we waste generation cycles on a Week 2 the user
    // can't access. Once they pay, SubscribeSuccess handles Week 2
    // generation on the return trip.
    if (dayNum === 5 && weekNum === 1 && !isPayingOrTrialing) {
      navigate('/paywall');
      return;
    }

    // If this was Day 5 of the week, generate Day 1 of the next week
    // before routing the user back to the plan.
    if (dayNum === 5) {
      const nextWeekNumber = weekNum + 1;
      try {
        console.log(
          '[DayDetail] Day 5 complete — generating Week',
          nextWeekNumber,
          'Day 1'
        );
        // Build no-repeat content set from EVERY week the user has
        // already received so the next week starts fresh.
        const allDays = (weeks || []).flatMap((w) => w.days || []);
        const usedContent = {
          scriptures: allDays
            .map((d) => d.scripture_reference)
            .filter(Boolean),
          themes: allDays.map((d) => d.title).filter(Boolean),
          pullQuotes: allDays
            .map((d) => d.pull_quote)
            .filter(Boolean),
        };
        const nextDay1 = await generateNextDay(
          userProfile,
          1,
          nextWeekNumber,
          [],
          dailyFeedback || [],
          intakeMessages || [],
          usedContent
        );
        addWeek(nextWeekNumber, {
          weekNumber: nextWeekNumber,
          week_title: `Week ${nextWeekNumber} — ${nextDay1.title || ''}`,
          week_theme: nextDay1.subtitle || '',
          days: [nextDay1],
          totalDays: 5,
        });
        setTimeout(
          () => navigate(`/study/week/${nextWeekNumber}`),
          800
        );
        return;
      } catch (err) {
        console.error(
          '[DayDetail] Error generating next week:',
          err
        );
      }
    }

    setTimeout(
      () =>
        navigate(
          weekNum === 1 ? '/study' : `/study/week/${weekNum}`
        ),
      800
    );
  };

  return (
    <div
      className="min-h-screen w-full ff-ui flex flex-col"
      style={{ background: PAPER, color: INK, position: 'relative' }}
    >
      <FontStyles />
      <DayStyles />

      {/* Hero block — dark color */}
      <div
        className="flex flex-col"
        style={{ backgroundColor: heroColor, color: 'white' }}
      >
        <div className="flex items-center justify-between px-5 pt-5 pb-2 w-full max-w-[460px] mx-auto">
          <button
            onClick={() => navigate('/study')}
            className="p-2 -ml-2 rounded-full transition active:scale-95"
          >
            <ChevronLeft size={20} strokeWidth={1.5} className="text-white/90" />
          </button>
          <div className="flex flex-col items-center gap-0.5">
            <div className="flex items-center gap-1.5">
              <FoxMark size={13} />
              <RootedWordmark
                size="sm"
                color="rgba(232,199,122,0.95)"
              />
            </div>
            <span
              className="ff-ui text-white/70"
              style={{ fontSize: 10, letterSpacing: '0.06em' }}
            >
              Day {dayNum} · Week {weekNum}
            </span>
          </div>
          <NavMenu variant="dark" />
        </div>

        <div className="px-7 pt-3 pb-3 grain relative w-full max-w-[460px] mx-auto">
          <div className="flex items-center gap-2">
            <span
              className="h-px flex-1"
              style={{ background: 'rgba(232,199,122,0.4)' }}
            />
            <span
              className="ff-mono text-white/70"
              style={{ fontSize: 10, letterSpacing: '0.22em' }}
            >
              WRITTEN FOR
            </span>
            <span
              className="h-px flex-1"
              style={{ background: 'rgba(232,199,122,0.4)' }}
            />
          </div>
          <div className="text-center mt-2">
            <div
              className="ff-display text-white leading-none"
              style={{
                fontSize: 30,
                fontWeight: 300,
                letterSpacing: '0.04em',
              }}
            >
              {userProfile?.name || 'You'}
            </div>
            {userProfile?.enneagram_type && (
              <div className="flex items-center justify-center gap-2 mt-2">
                <span
                  className="w-1 h-1 rounded-full"
                  style={{ background: '#E8C77A' }}
                />
                <span
                  className="ff-mono text-white/75"
                  style={{ fontSize: 9, letterSpacing: '0.22em' }}
                >
                  TYPE {userProfile.enneagram_type}
                  {userProfile.enneagram_wing
                    ? `w${userProfile.enneagram_wing}`
                    : ''}
                </span>
                <span
                  className="w-1 h-1 rounded-full"
                  style={{ background: '#E8C77A' }}
                />
              </div>
            )}
          </div>
        </div>

        <div className="px-7 pt-3 pb-7 grain relative w-full max-w-[460px] mx-auto">
          <div
            className="ff-mono text-white/55"
            style={{ fontSize: 10, letterSpacing: '0.15em' }}
          >
            DAY {dayNum} · {dow.toUpperCase()}
          </div>
          <h1
            className="ff-display mt-2 leading-[1.0] text-white"
            style={{ fontSize: 30, fontWeight: 500 }}
          >
            {day.title || `Day ${dayNum}`}
          </h1>
          {day.subtitle && (
            <div
              className="ff-display italic text-white/80 mt-2 leading-tight"
              style={{ fontSize: 13 }}
            >
              {day.subtitle}
            </div>
          )}
          <div
            className="mt-3 flex items-center gap-2 ff-mono text-[10px] text-white/70"
            style={{ letterSpacing: '0.1em' }}
          >
            <span>15–20 MIN</span>
            <span className="text-white/30">·</span>
            <span>{(day.scripture_reference || '').toUpperCase()}</span>
          </div>
        </div>
      </div>

      {/* Audio player bar — read-aloud controls.
          Gated by LISTEN_TO_STUDY_ENABLED (top of file) — hidden at
          launch to keep TTS costs off the free tier. */}
      {LISTEN_TO_STUDY_ENABLED && (
        <StudyAudioPlayer
          guideName={guide?.name}
          dayNumber={dayNum}
          isReading={isReading}
          isPaused={isPaused}
          currentLabel={currentChunkLabel}
          progress={readingProgress}
          finished={readingFinished}
          readingAmplitude={readingAmplitude}
          onStart={handleListenPress}
          onPause={pauseReading}
          onResume={resumeReading}
          onStop={stopReading}
        />
      )}

      {/* Body */}
      <div
        className="flex-1 overflow-y-auto scrollbar-hide grain relative"
        onMouseUp={captureSelection}
        onTouchEnd={captureSelection}
        style={{
          cursor: highlightMode ? 'text' : 'default',
        }}
      >
        <div className="w-full max-w-[460px] mx-auto px-7 pt-7 pb-32">
          {/* Highlights indicator + highlight-mode banner */}
          {(highlightCount > 0 || highlightMode) && (
            <div className="flex items-center justify-between mb-5">
              {highlightCount > 0 ? (
                <span
                  className="ff-mono inline-flex items-center gap-1.5"
                  style={{
                    color: GOLD_DEEP,
                    fontSize: 10,
                    letterSpacing: '0.18em',
                  }}
                >
                  <Bookmark
                    size={11}
                    strokeWidth={1.8}
                    fill={ACCENT}
                    stroke={ACCENT}
                  />
                  {highlightCount} HIGHLIGHT
                  {highlightCount === 1 ? '' : 'S'}
                </span>
              ) : (
                <span />
              )}
              {highlightMode && (
                <span
                  className="ff-mono"
                  style={{
                    color: ACCENT,
                    fontSize: 10,
                    letterSpacing: '0.18em',
                    background: 'rgba(166,124,58,0.10)',
                    padding: '4px 10px',
                    borderRadius: 999,
                  }}
                >
                  SELECT TEXT TO HIGHLIGHT
                </span>
              )}
            </div>
          )}

          {/* Opening Prayer */}
          {day.opening_prayer && (
            <div
              className="rounded-2xl p-5 text-center relative overflow-hidden"
              style={{
                background: 'rgba(166,124,58,0.07)',
                border: `1px solid rgba(166,124,58,0.25)`,
              }}
            >
              <div
                className="smallcaps ff-ui"
                style={{ color: GOLD_DEEP }}
              >
                Opening Prayer
              </div>
              <p
                className="ff-display italic mt-3 leading-[1.5]"
                style={{ color: INK, fontSize: 13.5, fontWeight: 400 }}
              >
                "{renderHighlighted(day.opening_prayer, dayHighlights.map((h) => h.text))}"
              </p>
            </div>
          )}

          {/* Scripture */}
          {(day.scripture_reference || day.scripture_text) && (
            <>
              <div className="mt-10 text-center">
                <div
                  className="ff-mono"
                  style={{
                    color: INK_SOFT,
                    fontSize: 10,
                    letterSpacing: '0.2em',
                  }}
                >
                  SCRIPTURE
                </div>
                {day.scripture_reference && (
                  <h2
                    className="ff-display italic mt-2 leading-tight"
                    style={{
                      color: ACCENT,
                      fontSize: 24,
                      fontWeight: 400,
                    }}
                  >
                    {day.scripture_reference}
                  </h2>
                )}
              </div>

              {day.scripture_text && (
                <div className="mt-6 pl-1">
                  <p
                    className="ff-display drop-cap leading-[1.55]"
                    style={{ color: INK, fontSize: 14.5, fontWeight: 400 }}
                  >
                    {renderHighlighted(day.scripture_text, dayHighlights.map((h) => h.text))}
                  </p>
                </div>
              )}

              <div className="hairline h-px my-9" />
            </>
          )}

          {/* Content sections */}
          {Array.isArray(day.content_sections) &&
            day.content_sections.map((sec, i) => (
              <div key={i} className={i > 0 ? 'mt-9' : ''}>
                {sec.heading && (
                  <h3
                    className="ff-display leading-tight"
                    style={{
                      color: INK,
                      fontSize: 19,
                      fontWeight: 500,
                      marginBottom: 12,
                    }}
                  >
                    {sec.heading}
                  </h3>
                )}
                {sec.body && (
                  <p
                    className="ff-ui leading-[1.65]"
                    style={{ color: INK_MID, fontSize: 13.5 }}
                  >
                    {renderHighlighted(sec.body, dayHighlights.map((h) => h.text))}
                  </p>
                )}
              </div>
            ))}

          {/* Enneagram insight */}
          {day.enneagram_insight && (
            <>
              <div className="hairline h-px my-9" />
              <div
                className="rounded-2xl p-5 relative overflow-hidden"
                style={{
                  background: 'rgba(166,124,58,0.07)',
                  border: `1px solid rgba(166,124,58,0.45)`,
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <BookOpen
                      size={12}
                      strokeWidth={1.6}
                      style={{ color: GOLD_DEEP }}
                    />
                    <span
                      className="smallcaps ff-ui"
                      style={{ color: GOLD_DEEP }}
                    >
                      For you
                    </span>
                  </div>
                  {userProfile?.enneagram_type && (
                    <span
                      className="ff-mono px-2 py-0.5 rounded-full"
                      style={{
                        background: ACCENT,
                        color: PAPER,
                        fontSize: 9,
                        letterSpacing: '0.1em',
                      }}
                    >
                      {(userProfile?.name || '').toUpperCase()} · {userProfile.enneagram_type}
                      {userProfile.enneagram_wing
                        ? `w${userProfile.enneagram_wing}`
                        : ''}
                    </span>
                  )}
                </div>
                <p
                  className="ff-display leading-[1.5]"
                  style={{ color: INK, fontSize: 14, fontWeight: 400 }}
                >
                  {renderHighlighted(day.enneagram_insight, dayHighlights.map((h) => h.text))}
                </p>
              </div>
            </>
          )}

          {/* Pull quote — card + share */}
          {day.pull_quote && (
            <div style={{ margin: '40px 0' }}>
              <div
                style={{
                  background: 'rgba(166,124,58,0.06)',
                  border: '1px solid rgba(166,124,58,0.2)',
                  borderRadius: 12,
                  padding: '16px 20px',
                  marginBottom: 10,
                }}
              >
                <p
                  className="ff-display italic"
                  style={{
                    fontSize: 17,
                    color: INK,
                    lineHeight: 1.7,
                    margin: 0,
                  }}
                >
                  "{renderHighlighted(day.pull_quote, dayHighlights.map((h) => h.text))}"
                </p>
              </div>
              <button
                type="button"
                onClick={() => sharePullQuote(day.pull_quote, { day: dayNum, week: weekNum })}
                style={{
                  background: 'none',
                  border: '1px solid rgba(166,124,58,0.3)',
                  borderRadius: 20,
                  padding: '8px 16px',
                  fontSize: 12,
                  color: '#A67C3A',
                  fontFamily: 'Arial, sans-serif',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="18" cy="5" r="3" />
                  <circle cx="6" cy="12" r="3" />
                  <circle cx="18" cy="19" r="3" />
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                </svg>
                Share this
              </button>
            </div>
          )}

          {/* Reflection questions */}
          {Array.isArray(day.reflection_questions) &&
            day.reflection_questions.length > 0 && (
              <div>
                <div
                  className="smallcaps ff-ui mb-5"
                  style={{ color: INK_SOFT }}
                >
                  Today's Reflection
                </div>
                <div className="space-y-5">
                  {day.reflection_questions.map((q, i) => (
                    <div key={i} className="flex gap-3">
                      <span
                        className="ff-mono flex-shrink-0 mt-1"
                        style={{ color: ACCENT, fontSize: 11 }}
                      >
                        Q{i + 1}
                      </span>
                      <p
                        className="ff-display flex-1 leading-[1.4]"
                        style={{ color: INK, fontSize: 15, fontWeight: 400 }}
                      >
                        {q}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* Closing practice */}
          {day.closing_practice && (
            <>
              <div className="hairline h-px my-9" />
              <div
                className="rounded-2xl p-5"
                style={{
                  background: PAPER_2,
                  border: `1px solid ${LINE}`,
                }}
              >
                <div
                  className="smallcaps ff-ui"
                  style={{ color: ACCENT }}
                >
                  Closing Practice
                </div>
                <p
                  className="ff-display mt-3 leading-[1.5]"
                  style={{ color: INK, fontSize: 14.5, fontWeight: 400 }}
                >
                  {renderHighlighted(day.closing_practice, dayHighlights.map((h) => h.text))}
                </p>
              </div>
            </>
          )}

          {/* Primary day-completion CTA — natural end of the day */}
          <div style={{ marginTop: 36 }}>
            <button
              type="button"
              onClick={dayIsComplete ? handleDoneNoCheckin : handleDoneAndCheckin}
              aria-label={dayIsComplete ? 'Done' : 'Done and Check In'}
              style={{
                background: '#1B1814',
                color: '#F2EBDC',
                border: 'none',
                borderRadius: 12,
                padding: '16px',
                fontSize: 16,
                cursor: 'pointer',
                fontWeight: 600,
                fontFamily: 'Georgia, serif',
                width: '100%',
                maxWidth: 440,
                display: 'block',
                margin: '0 auto',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              {dayIsComplete ? 'Done' : 'Done & Check In →'}
            </button>
          </div>

          {/* Brand sign-off */}
          <div className="mt-12 flex flex-col items-center gap-3">
            <FoxMark size={22} variant="gold-deep" />
            <div
              className="ff-display italic text-center"
              style={{ color: INK_SOFT, fontSize: 10 }}
            >
              It's not about who you are.
              <br />
              It's who you are becoming.
            </div>
          </div>
        </div>
      </div>

      {/* Fixed bottom action bar — pinned to viewport so it stays visible
          while the user scrolls through day content. */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: PAPER,
          borderTop: '1px solid rgba(166,124,58,0.15)',
          padding: '12px 20px',
          paddingBottom: 'calc(12px + env(safe-area-inset-bottom))',
          display: 'flex',
          gap: 10,
          alignItems: 'center',
          zIndex: 10,
        }}
      >
        {/* Journal button */}
        <button
          type="button"
          onClick={() => navigate('/journal')}
          style={{
            flex: 1,
            background: 'transparent',
            border: '1px solid rgba(166,124,58,0.3)',
            borderRadius: 10,
            padding: '10px 8px',
            fontSize: 12,
            color: '#A67C3A',
            fontFamily: 'Arial, sans-serif',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            cursor: 'pointer',
            position: 'relative',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          Journal
          {hasJournalForToday && (
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: 4,
                right: 6,
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: ACCENT,
              }}
            />
          )}
        </button>

        {/* Highlight button — toggles highlight mode */}
        <button
          type="button"
          onClick={() => setHighlightMode((v) => !v)}
          style={{
            flex: 1,
            background: highlightMode ? ACCENT : 'transparent',
            border: '1px solid rgba(166,124,58,0.3)',
            borderRadius: 10,
            padding: '10px 8px',
            fontSize: 12,
            color: highlightMode ? PAPER : '#A67C3A',
            fontFamily: 'Arial, sans-serif',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {highlightMode ? 'Done' : 'Highlight'}
        </button>

        {/* Done & Check In button (or Done on a returning visit to a
            completed day — same styling, different handler/label) */}
        <button
          type="button"
          onClick={dayIsComplete ? handleDoneNoCheckin : handleDoneAndCheckin}
          aria-label={dayIsComplete ? 'Done' : 'Done and Check In'}
          style={{
            flex: 2,
            background: '#1B1814',
            border: 'none',
            borderRadius: 10,
            padding: '10px 8px',
            fontSize: 13,
            color: '#F2EBDC',
            fontFamily: 'Georgia, serif',
            fontWeight: 600,
            cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {dayIsComplete ? 'Done' : 'Done & Check In →'}
        </button>
      </div>

      {/* Highlight toast */}
      {highlightToast && (
        <div
          className="fixed left-1/2 -translate-x-1/2 z-[60] ff-mono"
          style={{
            top: 80,
            background: ACCENT,
            color: PAPER,
            fontSize: 10.5,
            letterSpacing: '0.18em',
            padding: '8px 14px',
            borderRadius: 999,
            boxShadow: '0 8px 22px -8px rgba(166,124,58,0.55)',
          }}
        >
          ✓ HIGHLIGHTED
        </div>
      )}

      {showCheckin && (
        <CheckinSheet
          dayNumber={dayNum}
          dayContent={day}
          guide={guide}
          checkinAudioRef={checkinAudioRef}
          voice={voice}
          onClose={() => {
            cleanupCheckinAudio();
            // releaseAudioSession() dropped — see handleCheckinComplete.
            setShowCheckin(false);
          }}
          onComplete={handleCheckinComplete}
        />
      )}

      {showJournal && (
        <JournalSheet
          dayNumber={dayNum}
          day={day}
          existingEntries={dayJournalEntries}
          onClose={() => setShowJournal(false)}
          onSave={(text) => {
            addJournalEntry(dayNum, text);
            setShowJournal(false);
          }}
        />
      )}
    </div>
  );
}

// =====================================================================
// Journal sheet
// =====================================================================
function JournalSheet({ dayNumber, day, existingEntries, onClose, onSave }) {
  const [entry, setEntry] = useState('');
  const [savedToast, setSavedToast] = useState(false);

  const save = () => {
    const text = entry.trim();
    if (!text) return;
    setSavedToast(true);
    setTimeout(() => onSave(text), 900);
  };

  return (
    <div
      className="fixed inset-0 flex items-end md:items-center justify-center z-50"
      style={{ background: 'rgba(11,9,6,0.45)' }}
      onClick={onClose}
    >
      <div
        className="sheet-in w-full max-w-[460px] mx-auto rounded-t-3xl md:rounded-3xl flex flex-col"
        style={{
          background: PAPER,
          maxHeight: '88vh',
          minHeight: 460,
          boxShadow: '0 -20px 60px -10px rgba(0,0,0,0.35)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center justify-between px-5 pt-4 pb-3"
          style={{ borderBottom: `1px solid ${LINE}` }}
        >
          <div className="flex items-center gap-2">
            <Bookmark size={18} strokeWidth={1.6} style={{ color: ACCENT }} />
            <div>
              <div
                className="ff-mono"
                style={{
                  color: INK_SOFT,
                  fontSize: 9,
                  letterSpacing: '0.18em',
                }}
              >
                JOURNAL · DAY {dayNumber}
              </div>
              <div
                className="ff-display"
                style={{ color: INK, fontSize: 14, fontWeight: 500 }}
              >
                Your thoughts
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 -mr-2 rounded-full transition active:scale-95"
            aria-label="Close journal"
          >
            <X size={20} strokeWidth={1.5} style={{ color: INK_MID }} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto scrollbar-hide px-5 py-5">
          {existingEntries.length > 0 && (
            <div className="mb-5">
              <div
                className="smallcaps ff-ui mb-2"
                style={{ color: INK_SOFT }}
              >
                Earlier today
              </div>
              <div className="space-y-2">
                {existingEntries.map((e, i) => (
                  <div
                    key={i}
                    className="p-3 rounded-xl"
                    style={{
                      background: PAPER_2,
                      border: `1px solid ${LINE}`,
                    }}
                  >
                    <p
                      className="ff-display"
                      style={{
                        color: INK,
                        fontSize: 13.5,
                        lineHeight: 1.5,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {e.entry}
                    </p>
                    <div
                      className="ff-mono mt-1.5"
                      style={{
                        color: INK_SOFT,
                        fontSize: 9,
                        letterSpacing: '0.12em',
                      }}
                    >
                      {new Date(e.createdAt).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div
            className="ff-display"
            style={{
              color: INK,
              fontSize: 17,
              fontWeight: 500,
              marginBottom: 10,
            }}
          >
            What is coming up for you as you read today?
          </div>
          <textarea
            value={entry}
            onChange={(e) => setEntry(e.target.value)}
            rows={6}
            placeholder="Write freely. No one else will see this."
            className="w-full ff-display resize-none rounded-xl p-3 outline-none"
            style={{
              color: INK,
              fontSize: 14.5,
              lineHeight: 1.6,
              background: PAPER_2,
              border: `1px solid ${LINE}`,
              minHeight: 140,
              maxHeight: 260,
            }}
          />

          {Array.isArray(day?.reflection_questions) &&
            day.reflection_questions.length > 0 && (
              <div className="mt-5">
                <div
                  className="smallcaps ff-ui mb-3"
                  style={{ color: INK_SOFT }}
                >
                  Or reflect on one of these
                </div>
                <div className="space-y-2">
                  {day.reflection_questions.map((q, i) => (
                    <button
                      key={i}
                      onClick={() =>
                        setEntry((prev) =>
                          (prev ? prev + '\n\n' : '') + q + '\n'
                        )
                      }
                      className="w-full text-left p-3 rounded-xl transition active:scale-[0.99]"
                      style={{
                        background: 'rgba(166,124,58,0.06)',
                        border: `1px solid rgba(166,124,58,0.22)`,
                      }}
                    >
                      <span
                        className="ff-mono"
                        style={{
                          color: ACCENT,
                          fontSize: 10,
                          letterSpacing: '0.1em',
                          marginRight: 8,
                        }}
                      >
                        Q{i + 1}
                      </span>
                      <span
                        className="ff-display"
                        style={{
                          color: INK,
                          fontSize: 13.5,
                          lineHeight: 1.45,
                        }}
                      >
                        {q}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
        </div>

        <div
          className="px-5 pt-3 pb-5"
          style={{ borderTop: `1px solid ${LINE}` }}
        >
          {savedToast ? (
            <div
              className="text-center ff-mono py-3"
              style={{
                color: GOLD_DEEP,
                fontSize: 10.5,
                letterSpacing: '0.18em',
              }}
            >
              ✓ SAVED TO YOUR JOURNAL
            </div>
          ) : (
            <button
              onClick={save}
              disabled={!entry.trim()}
              className="w-full py-3.5 rounded-full ff-ui font-medium text-[14px] flex items-center justify-center gap-2 transition active:scale-[0.98]"
              style={{
                background: entry.trim() ? INK : 'rgba(27,24,20,0.15)',
                color: entry.trim() ? PAPER : INK_SOFT,
              }}
            >
              <Check size={15} strokeWidth={2} />
              Save to journal
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
